import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, GripVertical, Save } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { MenuService } from '../services/menuService';
import { createCategory, deleteCategory } from '../services/categoryManagementService';
import { Category, Language } from '../types/menu';

const SUPPORTED_LANGUAGES: Language[] = ['fr', 'ar', 'en'];

interface CategoryManagementProps {
  categories: Category[];
  onCategoryChange: (categories: Category[]) => void;
}

const CategoryManagement: React.FC<CategoryManagementProps> = ({ 
  categories,
  onCategoryChange 
}) => {
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createEmptyCategory = (): Category => ({
    id: '',
    name: '',
    order: categories.length,
    translations: { fr: '', ar: '', en: '' }
  });

  const handleSaveCategory = async (category: Category) => {
    try {
      setError(null);
      if (category.id) {
        // Update existing category
        await MenuService.saveCategory(category);
      } else {
        // Create new category
        await createCategory(category);
      }
      
      // Fetch updated categories and notify parent
      const updatedCategories = await MenuService.getCategories();
      onCategoryChange(updatedCategories);
      
      setEditingCategory(null);
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save the category. Please try again.');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        setError(null);
        await deleteCategory(id);
        
        // Fetch updated categories and notify parent
        const updatedCategories = await MenuService.getCategories();
        onCategoryChange(updatedCategories);
      } catch (err) {
        console.error('Error deleting category:', err);
        setError('Failed to delete the category. Please try again.');
      }
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const reorderedCategories = Array.from(categories);
    const [removed] = reorderedCategories.splice(result.source.index, 1);
    reorderedCategories.splice(result.destination.index, 0, removed);

    // Update order property
    const updatedCategories = reorderedCategories.map((cat, index) => ({
      ...cat,
      order: index
    }));

    // Save new order to database first, then sync parent state
    try {
      setError(null);
      await Promise.all(updatedCategories.map(category => MenuService.saveCategory(category)));
      const freshCategories = await MenuService.getCategories();
      onCategoryChange(freshCategories);
    } catch (err) {
      console.error('Failed to update category order:', err);
      setError('Failed to save the new category order.');
      // Re-sync with the server to roll back the optimistic reorder
      const freshCategories = await MenuService.getCategories();
      onCategoryChange(freshCategories);
    }
  };

  const renderCategoryCard = (category: Category, index: number) => {
    const draggableId = `category-${category.id.replace(/[^a-zA-Z0-9-]/g, '-')}`;
    
    return (
      <Draggable 
        key={draggableId}
        draggableId={draggableId}
        index={index}
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className="bg-white rounded-xl border border-stone-200 p-3 mb-2 hover:border-amber-300/70 hover:shadow-sm transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div {...provided.dragHandleProps} className="cursor-move" aria-hidden="true">
                  <GripVertical size={18} className="text-stone-300" />
                </div>
                <div className="flex items-center gap-2">
                  {category.emoji && <span className="text-xl leading-none" aria-hidden="true">{category.emoji}</span>}
                  <div>
                    <h3 className="font-semibold text-sm text-stone-800">{category.translations.fr}</h3>
                    <p className="text-xs text-stone-400">{category.translations.en}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setEditingCategory(category)}
                  aria-label={`Edit category ${category.translations.fr}`}
                  className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg"
                >
                  <Edit2 size={15} aria-hidden="true" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  aria-label={`Delete category ${category.translations.fr}`}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  const renderEditModal = () => {
    if (!editingCategory) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="category-dialog-title"
          className="bg-white rounded-2xl p-4 sm:p-6 max-w-lg w-full shadow-2xl"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 id="category-dialog-title" className="text-lg font-bold text-stone-800">
              {editingCategory.id ? 'Edit Category' : 'Add Category'}
            </h2>
            <button
              onClick={() => setEditingCategory(null)}
              aria-label="Close category editor"
              className="p-2 hover:bg-stone-100 rounded-lg text-stone-500"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>

          <div className="space-y-3">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <div key={lang}>
                <label htmlFor={`category-name-${lang}`} className="block text-sm font-medium mb-1 text-stone-600">
                  {lang}
                </label>
                <input
                  id={`category-name-${lang}`}
                  type="text"
                  value={editingCategory.translations[lang]}
                  onChange={(e) => {
                    setEditingCategory({
                      ...editingCategory,
                      translations: {
                        ...editingCategory.translations,
                        [lang]: e.target.value
                      }
                    });
                  }}
                  className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                  placeholder={`Category name in ${lang}`}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 mt-5">
            <button
              onClick={() => setEditingCategory(null)}
              className="px-4 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSaveCategory(editingCategory)}
              className="px-4 py-2 bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white rounded-lg shadow-sm hover:shadow-md text-sm font-medium flex items-center gap-2"
            >
              <Save size={16} aria-hidden="true" />
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-stone-200/70 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-stone-800">Categories</h2>
        <button
          onClick={() => setEditingCategory(createEmptyCategory())}
          className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white rounded-lg shadow-sm hover:shadow-md text-sm font-medium"
        >
          <Plus size={18} aria-hidden="true" />
          Add Category
        </button>
      </div>

      {error && (
        <div role="alert" className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {categories.length === 0 ? (
        <p className="text-stone-500 text-sm text-center py-6">No categories yet. Add your first one.</p>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="categories-list">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-2"
              >
                {categories.map((category, index) => renderCategoryCard(category, index))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {editingCategory && renderEditModal()}
    </div>
  );
};

export default CategoryManagement; 